import { ForbiddenPage, NotFoundPage } from '../../shared/ErrorPage';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Loading } from './components/ui';
import { useAuth } from './lib/auth';
import { AuditPage } from './pages/admin/AuditPage';
import { DsfaReportPage } from './pages/admin/DsfaReportPage';
import { RetentionPage } from './pages/admin/RetentionPage';
import { OperationalCalendar } from './pages/admin/OperationalCalendar';
import { CatalogsPage } from './pages/admin/CatalogsPage';
import { DocumentDesign } from './pages/admin/DocumentDesign';
import { GroupsPage } from './pages/admin/GroupsPage';
import { ReportingSettings } from './pages/admin/ReportingSettings';
import { RolesPage } from './pages/admin/RolesPage';
import { PortalSettings } from './pages/admin/PortalSettings';
import { AiSettingsPage } from './pages/admin/AiSettingsPage';
import { QrSettings } from './pages/admin/QrSettings';
import { SettingsPage } from './pages/admin/SettingsPage';
import { MeldekanaeleSettings } from './pages/admin/MeldekanaeleSettings';
import { TemplateSettings } from './pages/admin/TemplateSettings';
import { TenantsPage } from './pages/admin/TenantsPage';
import { UserDetailPage } from './pages/admin/UserDetailPage';
import { UsersPage } from './pages/admin/UsersPage';
import { ContractingPage } from './pages/ContractingPage';
import { CustomersPage } from './pages/CustomersPage';
import { DashboardPage } from './pages/DashboardPage';
import { DispatchPage } from './pages/DispatchPage';
import { InboxPage } from './pages/InboxPage';
import { LoginPage } from './pages/LoginPage';
import { PasswordChangePage } from './pages/PasswordChangePage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { MaintenancePage } from './pages/MaintenancePage';
import { MeterReadingEntryPage } from './pages/MeterReadingEntryPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { MaterialPage } from './pages/MaterialPage';
import { ProcurementPage } from './pages/ProcurementPage';
import { ProfilePage } from './pages/ProfilePage';
import { PropertiesPage } from './pages/PropertiesPage';
import { PropertyDetailPage } from './pages/PropertyDetailPage';
import { ReportsPage } from './pages/ReportsPage';
import { RoutePlannerPage } from './pages/RoutePlannerPage';
import { TaskDetailPage } from './pages/TaskDetailPage';
import { TaskNewPage } from './pages/TaskNewPage';
import { TasksPage } from './pages/TasksPage';
import { UtilitySuppliersPage } from './pages/UtilitySuppliersPage';
import { UtilityTariffsPage } from './pages/UtilityTariffsPage';
import { VehiclesPage } from './pages/VehiclesPage';
import { WarehousesPage } from './pages/WarehousesPage';
import { TimePage } from './pages/TimePage';

/** Schützt eine Route: Anmeldung + optional ein erforderliches Recht. */
function Protected({ permission, children }: { permission?: string; children: JSX.Element }) {
  const { user, loading, can, isParked } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;

  // Stillgelegtes Konto: kein Mandant, keine Fachdaten. Erreichbar bleiben das
  // Lagebild – dort stehen leere Kacheln – und das eigene Profil. Alles andere
  // führt dorthin zurück, statt eine Seite zu zeigen, die nichts anzeigen kann.
  //
  // Nur für Bereiche mit Rechtebedarf: Ohne `permission` ist dies die Hülle
  // (Menü und Rahmen). Würde sie mitumgeleitet, drehte sich die Umleitung im
  // Kreis, weil auch das Ziel innerhalb der Hülle liegt.
  if (isParked && permission) return <Navigate to="/profile" replace />;
  if (permission && !can(permission)) {
    return (
      <ForbiddenPage
        detail={
          <>
            Für diesen Bereich fehlt Ihnen die Berechtigung <span className="mono">{permission}</span>. Wenden Sie sich bei
            Bedarf an die Administration.
          </>
        }
      />
    );
  }
  return children;
}

export default function App() {
  const { user, loading } = useAuth();

  if (loading) return <Loading label="FacilityFlow wird gestartet …" />;

  // Erzwungener Passwortwechsel geht jeder Route vor (Review M1). Die
  // Middleware lässt in diesem Zustand ohnehin nur noch das eigene Profil,
  // die Passwortänderung und das Abmelden durch – jede andere Seite liefe
  // in eine Fehlermeldung, deren Ursache niemand erraten könnte.
  if (user?.must_change_password) return <PasswordChangePage />;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      {/* Öffentlich, weil wer sein Passwort vergessen hat, sich gerade nicht
          anmelden kann (Review K3). Nimmt den Token aus der Adresszeile. */}
      <Route path="/reset" element={<ResetPasswordPage />} />

      <Route
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="tasks" element={<Protected permission="tasks.read"><TasksPage /></Protected>} />
        <Route path="tasks/new" element={<Protected permission="tasks.create"><TaskNewPage /></Protected>} />
        <Route path="tasks/:id" element={<Protected permission="tasks.read"><TaskDetailPage /></Protected>} />
        <Route path="dispatch" element={<Protected permission="dispatch.board"><DispatchPage /></Protected>} />
        <Route path="eingang" element={<Protected permission="dispatch.board"><InboxPage /></Protected>} />
        <Route path="routes" element={<Protected permission="dispatch.route"><RoutePlannerPage /></Protected>} />
        <Route path="properties" element={<Protected permission="properties.read"><PropertiesPage /></Protected>} />
        <Route path="properties/:id" element={<Protected permission="properties.read"><PropertyDetailPage /></Protected>} />
        <Route path="customers" element={<Protected permission="customers.read"><CustomersPage /></Protected>} />
        <Route path="warehouses" element={<Protected permission="material.read"><WarehousesPage /></Protected>} />
        <Route path="vehicles" element={<Protected permission="fleet.read"><VehiclesPage /></Protected>} />
        <Route path="material" element={<Protected permission="material.read"><MaterialPage /></Protected>} />
        <Route path="invoices" element={<Protected permission="material.read"><InvoicesPage /></Protected>} />
        <Route path="contracting" element={<Protected permission="contracting.read"><ContractingPage /></Protected>} />
        <Route path="contractors" element={<Protected permission="contracting.read"><ContractingPage view="dienstleister" /></Protected>} />
        <Route path="utility-suppliers" element={<Protected permission="utilities.read"><UtilitySuppliersPage /></Protected>} />
        <Route path="utility-tariffs" element={<Protected permission="utilities.read"><UtilityTariffsPage /></Protected>} />
        <Route path="procurement" element={<Protected permission="procurement.read"><ProcurementPage /></Protected>} />
        <Route path="time" element={<Protected permission="time.read"><TimePage /></Protected>} />
        <Route path="reports" element={<Protected permission="reports.dashboard"><ReportsPage /></Protected>} />
        <Route path="maintenance" element={<Protected permission="maintenance.read"><MaintenancePage /></Protected>} />
        <Route path="meter-readings" element={<Protected permission="properties.readings"><MeterReadingEntryPage /></Protected>} />
        <Route path="profile" element={<ProfilePage />} />

        <Route path="admin/users" element={<Protected permission="admin.users.read"><UsersPage /></Protected>} />
        <Route path="admin/users/:id" element={<Protected permission="admin.users.read"><UserDetailPage /></Protected>} />
        <Route path="admin/groups" element={<Protected permission="admin.groups.read"><GroupsPage /></Protected>} />
        <Route path="admin/roles" element={<Protected permission="admin.roles.read"><RolesPage /></Protected>} />
        <Route path="admin/catalogs" element={<Protected permission="admin.catalogs.read"><CatalogsPage /></Protected>} />
        <Route path="admin/calendar" element={<Protected permission="admin.catalogs.read"><OperationalCalendar /></Protected>} />
        <Route path="admin/tenants" element={<Protected permission="admin.tenants"><TenantsPage /></Protected>} />
        <Route path="admin/audit" element={<Protected permission="admin.audit"><AuditPage /></Protected>} />
        <Route path="admin/dsfa" element={<Protected permission="admin.settings"><DsfaReportPage /></Protected>} />
        <Route path="admin/retention" element={<Protected permission="admin.settings"><RetentionPage /></Protected>} />
        <Route path="admin/settings" element={<Protected permission="admin.settings"><SettingsPage /></Protected>} />
        <Route path="admin/channels" element={<Protected permission="admin.settings"><MeldekanaeleSettings /></Protected>} />
        <Route path="admin/portal" element={<Protected permission="admin.settings"><PortalSettings /></Protected>} />
        <Route path="admin/ai-settings" element={<Protected permission="admin.ai"><AiSettingsPage /></Protected>} />
        <Route path="admin/qr" element={<Protected permission="properties.update"><QrSettings /></Protected>} />
        <Route path="admin/push" element={<Protected permission="admin.settings"><TemplateSettings /></Protected>} />
        <Route path="admin/notifications" element={<Protected permission="admin.settings"><ReportingSettings /></Protected>} />
        <Route path="admin/documents" element={<Protected permission="admin.settings"><DocumentDesign /></Protected>} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
